// Staff games view - game management across all users

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

/**
 * Staff games view component for managing all games
 */
class StaffGamesView extends BaseComponent {
  static properties = {
    _games: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _confirmDeleteId: { type: String, state: true },
    _confirmDeleteAll: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .game-id {
      font-family: monospace;
      font-size: 0.8rem;
      color: #6c757d;
    }

    .delete-all-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }

    tr.deleted-game {
      color: #6c757d;
      font-style: italic;
    }

    tr.deleted-game .game-id {
      text-decoration: line-through;
    }

    .deleted-badge {
      font-size: 0.7rem;
      padding: 0.15rem 0.4rem;
      border-radius: 0.25rem;
      background: #dee2e6;
      color: #495057;
      margin-left: 0.5rem;
      font-style: normal;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Game>} */
    this._games = [];
    this._loading = true;
    this._error = null;
    this._confirmDeleteId = null;
    this._confirmDeleteAll = false;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadGames();
  }

  async _loadGames() {
    this._loading = true;
    this._error = null;

    try {
      this._games = await api.listAllGames();
    } catch (err) {
      console.error('Failed to load games:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load games';
    } finally {
      this._loading = false;
    }
  }

  /** @param {string} gameId */
  async _deleteGame(gameId) {
    try {
      await api.deleteGameAdmin(gameId);
      this._confirmDeleteId = null;
      await this._loadGames();
    } catch (err) {
      console.error('Failed to delete game:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete game';
    }
  }

  async _deleteAllGames() {
    try {
      await api.deleteAllGames();
      this._confirmDeleteAll = false;
      await this._loadGames();
    } catch (err) {
      console.error('Failed to delete all games:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete all games';
    }
  }

  /** @param {string | undefined} dateString */
  _formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  render() {
    return html`
      ${this._error ? html` <div class="alert alert-danger" role="alert">${this._error}</div> ` : ''}

      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">All Games</h5>
          ${
            !this._loading && this._games.length > 0
              ? html`
                ${
                  this._confirmDeleteAll
                    ? html`
                      <div>
                        <span class="text-danger me-2">Delete all ${this._games.length} games?</span>
                        <button class="btn btn-sm btn-danger me-1" @click="${this._deleteAllGames}">Confirm</button>
                        <button class="btn btn-sm btn-secondary" @click="${() => (this._confirmDeleteAll = false)}">
                          Cancel
                        </button>
                      </div>
                    `
                    : html`
                      <button class="btn btn-sm btn-outline-danger" @click="${() => (this._confirmDeleteAll = true)}">
                        Delete All Games
                      </button>
                    `
                }
              `
              : ''
          }
        </div>
        <div class="card-body">
          ${
            this._loading
              ? html`
                <div class="text-center py-4">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2">Loading games...</p>
                </div>
              `
              : this._games.length === 0
                ? html` <p class="text-muted">No games yet.</p> `
                : html`
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th>Adventure</th>
                          <th>Party Leader</th>
                          <th>Game ID</th>
                          <th>Players</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${this._games.map(
                          (game) => html`
                            <tr class="${game.deletedAt ? 'deleted-game' : ''}">
                              <td>
                                ${game.adventureName}
                                ${
                                  game.deletedAt
                                    ? html`<span class="deleted-badge" title="Deleted ${this._formatDate(game.deletedAt)}"
                                      >deleted</span
                                    >`
                                    : ''
                                }
                              </td>
                              <td>${game.ownerUsername}</td>
                              <td><span class="game-id">${game.id}</span></td>
                              <td>${game.playerCount}</td>
                              <td>${this._formatDate(game.createdAt)}</td>
                              <td>
                                <a
                                  class="btn btn-sm btn-outline-primary me-1"
                                  href="/spectate/${game.id}"
                                  @click="${(/** @type {Event} */ e) => {
                                    e.preventDefault();
                                    router.navigate(`/spectate/${game.id}`);
                                  }}"
                                >
                                  Watch
                                </a>
                                <a
                                  class="btn btn-sm btn-outline-secondary me-1"
                                  href="/staff/games/${game.id}/events"
                                  @click="${(/** @type {Event} */ e) => {
                                    e.preventDefault();
                                    router.navigate(`/staff/games/${game.id}/events`);
                                  }}"
                                >
                                  View Events
                                </a>
                                ${
                                  game.deletedAt
                                    ? ''
                                    : this._confirmDeleteId === game.id
                                      ? html`
                                        <button
                                          class="btn btn-sm btn-danger me-1"
                                          @click="${() => this._deleteGame(game.id)}"
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          class="btn btn-sm btn-secondary"
                                          @click="${() => (this._confirmDeleteId = null)}"
                                        >
                                          Cancel
                                        </button>
                                      `
                                      : html`
                                        <button
                                          class="btn btn-sm btn-outline-danger"
                                          @click="${() => (this._confirmDeleteId = game.id)}"
                                        >
                                          Delete
                                        </button>
                                      `
                                }
                              </td>
                            </tr>
                          `,
                        )}
                      </tbody>
                    </table>
                  </div>
                `
          }
        </div>
      </div>
    `;
  }
}

customElements.define('staff-games-view', StaffGamesView);
